import { createSlice } from '@reduxjs/toolkit';

import { findComment } from '../../utils/util';

export const initialState = {
  organisations: [],
  persons: [],
  posts: [],
  reactions: [],
  filters: {},
  page: 0,
  product: undefined,
  post: undefined,
};

export const marketBuzzSlice = createSlice({
  name: 'market-buzz',
  initialState,
  reducers: {
    setOrganisations: (state, action) => {
      state.organisations = action.payload;
    },
    setPersons: (state, action) => {
      state.persons = action.payload;
    },
    setPosts: (state, action) => {
      state.posts = action.payload;
    },
    amendPosts: (state, action) => {
      state.posts = [...state.posts, ...action.payload];
    },
    addPost: (state, action) => {
      state.posts.unshift(action.payload);
    },
    replacePost: (state, action) => {
      const post = action.payload;

      const foundPost = state.posts.find((postInState) => postInState.externalReference === post.externalReference);
      const index = state.posts.findIndex((postInState) => postInState.externalReference === post.externalReference);

      if (index !== -1) {
        post.comments = foundPost.comments;
        state.posts[index] = post;
      }
    },
    deletePost: (state, action) => {
      state.posts = state.posts.filter((post) => post.externalReference !== action.payload);
    },
    setReactions: (state, action) => {
      state.reactions = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    addCommentsToPost: (state, action) => {
      const { postExternalReference, comments } = action.payload;
      const post = state.posts.find((post) => post.externalReference === postExternalReference);

      if (post && post.comments) {
        for (const comment of comments) {
          const foundComment = post.comments.find(
            (postComment) => postComment.externalReference === comment.externalReference
          );

          if (foundComment) continue;

          post.comments.push(comment);
        }
      } else if (post) {
        post.comments = comments;
      }
    },
    addNewCommentToPost: (state, action) => {
      const { postExternalReference, comment } = action.payload;
      const post = state.posts.find((post) => post.externalReference === postExternalReference);

      if (post && post.comments) {
        post.comments.unshift(comment);
      } else if (post) {
        post.comments = [comment];
      }

      post.totalComments += 1;
    },
    addCommentsToComment: (state, action) => {
      const { parentPostExternalReference, commentExternalReference, comments } = action.payload;
      const post = state.posts.find((post) => post.externalReference === parentPostExternalReference);

      if (post) {
        const commentToUpdate = findComment(post.comments, commentExternalReference);

        if (commentToUpdate && commentToUpdate.comments) {
          for (const comment of comments) {
            if (!comment.comments) continue;

            const foundComment = comment.comments.find(
              (childComment) => childComment.externalReference === comment.externalReference
            );

            if (foundComment) continue;

            commentToUpdate.comments.push(comment);
          }
        } else if (commentToUpdate) {
          commentToUpdate.comments = comments;
        }
      }
    },
    addNewCommentToComment: (state, action) => {
      const { postExternalReference, commentExternalReference, comment } = action.payload;

      const post = state.posts.find((post) => post.externalReference === postExternalReference);

      if (post) {
        const commentToUpdate = findComment(post.comments, commentExternalReference);

        if (commentToUpdate && commentToUpdate.comments) {
          commentToUpdate.comments.unshift(comment);
        } else if (commentToUpdate) {
          commentToUpdate.comments = [comment];
        }

        post.totalComments += 1;
      }
    },
    replaceComment: (state, action) => {
      const { postExternalReference, parentExternalReference, comment } = action.payload;

      const post = state.posts.find((post) => post.externalReference === postExternalReference);

      if (post && postExternalReference !== parentExternalReference) {
        const parentComment = findComment(post.comments, parentExternalReference);

        if (parentComment) {
          const foundComponent = parentComment.comments.find(
            (commentToUpdate) => commentToUpdate.externalReference === comment.externalReference
          );
          const foundIndex = parentComment.comments.findIndex(
            (commentToUpdate) => commentToUpdate.externalReference === comment.externalReference
          );

          if (foundComponent) {
            comment.comments = foundComponent.comments;
            parentComment.comments[foundIndex] = comment;
          }
        }
      } else if (post) {
        const foundComponent = post.comments.find(
          (commentToUpdate) => commentToUpdate.externalReference === comment.externalReference
        );
        const foundIndex = post.comments.findIndex(
          (commentToUpdate) => commentToUpdate.externalReference === comment.externalReference
        );

        if (foundComponent) {
          comment.comments = foundComponent.comments;
          post.comments[foundIndex] = comment;
        }
      }
    },
    deleteComment: (state, action) => {
      const { postExternalReference, parentExternalReference, commentExternalReference } = action.payload;

      const post = state.posts.find((post) => post.externalReference === postExternalReference);

      if (post && postExternalReference !== parentExternalReference) {
        const foundComment = findComment(post.comments, parentExternalReference);

        if (foundComment) {
          foundComment.comments = foundComment.comments.filter((comment) => {
            if (comment.externalReference !== commentExternalReference) {
              return true;
            } else {
              post.totalComments -= comment.totalComments + 1;
              return false;
            }
          });
        }
      } else if (post) {
        post.comments = post.comments.filter((comment) => {
          if (comment.externalReference !== commentExternalReference) {
            return true;
          } else {
            post.totalComments -= comment.totalComments + 1;
            return false;
          }
        });
      }
    },
    addReactionToPost: (state, action) => {
      const { reactionExternalReference, reaction, postExternalReference } = action.payload;
      const post = state.posts.find((post) => post.externalReference === postExternalReference);

      if (post) {
        const foundReaction = post.reactionCount.find((reactionCount) => reactionCount.text === reaction.reaction);

        if (foundReaction) {
          foundReaction.count += 1;
        } else {
          post.reactionCount.push({ count: 1, text: reaction.reaction });
        }

        delete reaction.personExternalReference;

        state.reactions.push({ ...reaction, externalReference: reactionExternalReference });
      }
    },
    deleteReactionFromPost: (state, action) => {
      const { reactionExternalReference, reactionType, postExternalReference } = action.payload;
      const post = state.posts.find((post) => post.externalReference === postExternalReference);

      if (post) {
        const foundReaction = post.reactionCount.find((reactionCount) => reactionCount.text === reactionType);

        if (foundReaction && foundReaction.count > 0) {
          foundReaction.count -= 1;
        }

        state.reactions = state.reactions.filter(
          (reaction) => reaction.externalReference !== reactionExternalReference
        );
      }
    },
    setProduct: (state, action) => {
      state.product = action.payload;
    },
    setPost: (state, action) => {
      state.post = action.payload;
    },
    resetMarketBuzzSlice: () => initialState,
  },
});

export const {
  setOrganisations,
  setPersons,
  setPosts,
  amendPosts,
  addPost,
  replacePost,
  deletePost,
  setReactions,
  setFilters,
  setPage,
  addCommentsToPost,
  addNewCommentToPost,
  addCommentsToComment,
  addNewCommentToComment,
  replaceComment,
  deleteComment,
  addReactionToPost,
  deleteReactionFromPost,
  setProduct,
  setPost,
  resetMarketBuzzSlice,
} = marketBuzzSlice.actions;

export default marketBuzzSlice.reducer;
